import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp, faFacebookF, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { faEnvelope, faPhone, faComments, faTimes, faPlus } from '@fortawesome/free-solid-svg-icons';

// Constants
const CONTACT_DATA = {
  phoneNumber: '+918093237123',
  emailAddress: 'corenovarenewable@gmail.com',
};

const CONTACT_ITEMS = [
  {
    id: 'whatsapp',
    icon: faWhatsapp,
    label: 'WhatsApp',
    bgClass: 'bg-[#25D366] hover:bg-[#20B954]',
    url: `https://wa.me/${CONTACT_DATA.phoneNumber.replace(/[^\d]/g, '')}`,
    delay: 0
  },
  {
    id: 'facebook',
    icon: faFacebookF,
    label: 'Facebook',
    bgClass: 'bg-[#1877F2] hover:bg-[#166FE5]',
    url: 'https://www.facebook.com/profile.php?id=61579266181929',
    delay: 50
  },
  {
    id: 'instagram',
    icon: faInstagram,
    label: 'Instagram',
    bgClass: 'bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF] hover:from-[#E8791F] hover:via-[#C91E6B] hover:to-[#7129A0]',
    url: 'https://www.instagram.com/corenova.re/',
    delay: 100
  },
  {
    id: 'email',
    icon: faEnvelope,
    label: 'Email',
    bgClass: 'bg-rose-500 hover:bg-rose-600',
    url: `mailto:${CONTACT_DATA.emailAddress}`,
    delay: 150
  },
  {
    id: 'phone',
    icon: faPhone,
    label: 'Call',
    bgClass: 'bg-green-600 hover:bg-green-700',
    url: `tel:${CONTACT_DATA.phoneNumber}`,
    delay: 200
  }
];

// Sub-components
const ContactIcon = ({ item, index, isOpen, onClose }) => {
  const handleClick = (e) => {
    e.stopPropagation();
    
    if (item.id === 'email' || item.id === 'phone') {
      window.location.href = item.url;
    } else {
      window.open(item.url, '_blank', 'noopener,noreferrer');
    }
    
    // Close menu after a short delay for better UX
    setTimeout(() => onClose(), 150);
  };

  const translateY = isOpen ? -(index + 1) * 65 : 0;

  return (
    <div
      className={`absolute bottom-0 right-0 transition-all duration-500 ease-out ${
        isOpen 
          ? 'opacity-100 scale-100 pointer-events-auto' 
          : 'opacity-0 scale-75 pointer-events-none'
      }`}
      style={{
        transform: `translateY(${translateY}px)`,
        transitionDelay: isOpen ? `${item.delay}ms` : `${(CONTACT_ITEMS.length - index - 1) * 50}ms`
      }}
    >


      {/* Contact Button */}
      <button
        onClick={handleClick}
        aria-label={`Contact via ${item.label}`}
        className={`
          w-12 h-12 rounded-full flex items-center justify-center 
          shadow-lg hover:shadow-xl transition-all duration-300 
          text-white relative overflow-hidden group
          ${item.bgClass}
          hover:scale-110 active:scale-95
          before:absolute before:inset-0 before:bg-white before:opacity-0
          before:transition-opacity before:duration-300 hover:before:opacity-20
        `}
      >
        <FontAwesomeIcon 
          icon={item.icon} 
          className="text-lg relative z-10 transition-transform duration-300 group-hover:scale-110" 
        />
        
        {/* Ripple effect */}
        <div className="absolute inset-0 rounded-full bg-white opacity-0 scale-0 group-active:opacity-30 group-active:scale-100 transition-all duration-150"></div>
      </button>
    </div>
  );
};

const MainActionButton = ({ isOpen, onClick, contactCount }) => (
  <div className="relative">
    {/* Main Button */}
    <button
      onClick={onClick}
      aria-label={isOpen ? "Close contact menu" : "Open contact menu"}
      className={`
        w-14 h-14 rounded-full flex items-center justify-center 
        shadow-lg hover:shadow-xl transition-all duration-300 text-white 
        bg-gradient-to-r from-primary-green to-secondary-blue
        hover:scale-110 active:scale-95 group relative overflow-hidden
        ${isOpen ? 'rotate-45' : 'rotate-0'}
        before:absolute before:inset-0 before:bg-white before:opacity-0
        before:transition-opacity before:duration-300 hover:before:opacity-20
      `}
    >
      <FontAwesomeIcon 
        icon={isOpen ? faPlus : faComments} 
        className={`
          text-xl transition-all duration-300 relative z-10
          ${isOpen ? 'rotate-45' : 'rotate-0'}
          group-hover:scale-110
        `} 
      />
      
      {/* Pulse effect when closed */}
      {!isOpen && (
        <>
          <div className="absolute inset-0 rounded-full border-2 border-primary-green animate-ping opacity-30"></div>
          <div className="absolute -inset-1 rounded-full border border-primary-green/50 animate-pulse opacity-40"></div>
        </>
      )}

      {/* Ripple effect */}
      <div className="absolute inset-0 rounded-full bg-white opacity-0 scale-0 group-active:opacity-30 group-active:scale-100 transition-all duration-200"></div>
    </button>

    {/* Contact count badge */}
    {!isOpen && (
      <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-md animate-bounce">
        {contactCount}
      </div>
    )}
  </div>
);

const BackgroundOverlay = ({ isOpen, onClick }) => (
  <div
    className={`
      fixed inset-0 transition-all duration-300 z-[59]
      ${isOpen 
        ? 'bg-black/20 backdrop-blur-sm opacity-100 pointer-events-auto' 
        : 'bg-transparent opacity-0 pointer-events-none'
      }
    `}
    onClick={onClick}
  />
);

// Main Component
const FloatingContacts = () => {
  const [isOpen, setIsOpen] = useState(false);
  
  const toggleMenu = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = () => {
      if (isOpen) {
        closeMenu();
      }
    };

    if (isOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isOpen]);

  // Close menu on escape key
  React.useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeMenu();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [isOpen]);

  return (
    <>
      {/* Background Overlay */}
      <BackgroundOverlay isOpen={isOpen} onClick={closeMenu} />
      
      {/* Floating Action Button Container */}
      <div 
        className="fixed bottom-5 right-5 md:bottom-8 md:right-8 z-[60]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative">
          {/* Contact Icons */}
          {CONTACT_ITEMS.map((item, index) => (
            <ContactIcon
              key={item.id}
              item={item}
              index={index}
              isOpen={isOpen}
              onClose={closeMenu}
            />
          ))}

          {/* Main Action Button */}
          <MainActionButton 
            isOpen={isOpen} 
            onClick={toggleMenu} 
            contactCount={CONTACT_ITEMS.length}
          />


        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes slideInUp {
          from {
            transform: translateY(100%) scale(0.8);
            opacity: 0;
          }
          to {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
        }

        @keyframes slideOutDown {
          from {
            transform: translateY(0) scale(1);
            opacity: 1;
          }
          to {
            transform: translateY(100%) scale(0.8);
            opacity: 0;
          }
        }

        .animate-slide-in {
          animation: slideInUp 0.3s ease-out forwards;
        }

        .animate-slide-out {
          animation: slideOutDown 0.3s ease-in forwards;
        }
      `}</style>
    </>
  );
};

export default FloatingContacts;